import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { notifyUnsaveableDraft } from './useSaveRunToasts';
import type { Store } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store/index';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { getQuote } from '../services/quotes';
import { QuoteConflictError, RetiredSaveError } from '../services/quoteSaveErrors';
import { beginServerHydration, reloadDraft } from '../store/quoteSlice';
import { hydrateLoadedQuote } from '../store/hydrateLoadedQuote';
import type { MountLoadMode } from '../utils/mountLoadMode';
import type { QuoteData } from '../types/quote.types';
import type { QuoteSaveLaneApi } from './useQuoteSaveLane';
import { useQuoteStepOrder } from './useQuoteStepOrder';

export type ReloadPhase =
    | 'mount-loading'
    | 'ready'
    | 'mount-failed'
    | 'conflict'
    | 'reloading'
    | 'reload-failed';

export interface QuoteReload {
    phase: ReloadPhase;
    /** True once the wizard has been shown for this draft, until the next reload begins. */
    wizardShown: boolean;
    conflictStatus: QuoteData['status'];
    confirmReload: () => void;
    retryMountLoad: () => void;
}

type LoadMode = Extract<MountLoadMode, 'refetch' | 'flush-then-refetch'>;

const isTerminalLoadStop = (error: unknown): boolean =>
    error instanceof RetiredSaveError || error instanceof QuoteConflictError;

const toQuoteStatus = (status: string | undefined): QuoteData['status'] =>
    status === 'completed' || status === 'dead' || status === 'live' ? status : undefined;

const initialPhase = (mode: MountLoadMode): ReloadPhase =>
    mode === 'refetch' || mode === 'flush-then-refetch' ? 'mount-loading' : 'ready';

interface LoadOrigin {
    draftId: string;
    quoteId: string;
}

const readOrigin = (store: Store<RootState>): LoadOrigin | null => {
    const { draftId, quoteId } = store.getState().quote;
    return quoteId ? { draftId, quoteId } : null;
};

const isSameOrigin = (store: Store<RootState>, origin: LoadOrigin): boolean => {
    const { draftId, quoteId } = store.getState().quote;
    return draftId === origin.draftId && quoteId === origin.quoteId;
};

export const useQuoteReload = (
    mountLoadMode: MountLoadMode,
    saveLane: QuoteSaveLaneApi
): QuoteReload => {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const draftId = useAppSelector((state) => state.quote.draftId);
    const { order, defer, settled } = useQuoteStepOrder();
    const [phase, setPhase] = useState<ReloadPhase>(() => initialPhase(mountLoadMode));
    const [wizardShown, setWizardShown] = useState(() => initialPhase(mountLoadMode) === 'ready');
    const [conflictStatus, setConflictStatus] = useState<QuoteData['status']>(undefined);
    const loadMode = useRef<LoadMode>(
        mountLoadMode === 'flush-then-refetch' ? 'flush-then-refetch' : 'refetch'
    );
    const lane = useRef(saveLane);
    lane.current = saveLane;

    const { conflict, laneDraftId } = saveLane;
    const awaitingHomeDealer =
        mountLoadMode === 'flush-then-refetch' && saveLane.awaitingHomeDealer;
    const laneReady = laneDraftId !== null && laneDraftId === draftId;

    useEffect(() => {
        if (phase === 'ready') setWizardShown(true);
    }, [phase]);

    useEffect(() => {
        if (conflict === null || phase === 'reloading' || phase === 'reload-failed') return;
        setConflictStatus(toQuoteStatus(conflict.quoteStatus));
        setPhase('conflict');
    }, [conflict, phase]);

    useEffect(() => {
        if (phase !== 'mount-loading' || !settled || !laneReady || awaitingHomeDealer)
            return undefined;
        const origin = readOrigin(store);
        if (!origin) {
            setPhase('ready');
            return undefined;
        }
        let superseded = false;
        const isCurrent = () => !superseded && isSameOrigin(store, origin);
        const run = async (): Promise<void> => {
            if (loadMode.current === 'flush-then-refetch') {
                if (!lane.current.canSave) {
                    notifyUnsaveableDraft();
                    setPhase('ready');
                    return;
                }
                try {
                    await lane.current.flush({ quiet: true });
                } catch (error) {
                    if (isCurrent() && !isTerminalLoadStop(error)) setPhase('ready');
                    return;
                }
                if (!isCurrent()) return;
            }
            try {
                const quote = await lane.current.guard(() => getQuote(origin.quoteId));
                if (!isCurrent()) return;
                const step = store.getState().quote.currentStep;
                dispatch(beginServerHydration());
                hydrateLoadedQuote(dispatch, quote, {
                    order,
                    defer,
                    landing: { kind: 'keep', step },
                    announce: false,
                });
                setPhase('ready');
            } catch (error) {
                if (isCurrent() && !(error instanceof RetiredSaveError)) setPhase('mount-failed');
            }
        };
        void run();
        return () => {
            superseded = true;
        };
    }, [phase, settled, laneReady, awaitingHomeDealer, store, dispatch, order, defer]);

    useEffect(() => {
        if (phase !== 'reloading' || !settled || !laneReady) return undefined;
        const origin = readOrigin(store);
        if (!origin) {
            setPhase('ready');
            return undefined;
        }
        let superseded = false;
        const isCurrent = () => !superseded && isSameOrigin(store, origin);
        const run = async (): Promise<void> => {
            try {
                const quote = await lane.current.guard(() => getQuote(origin.quoteId));
                if (!isCurrent()) return;
                dispatch(beginServerHydration());
                hydrateLoadedQuote(dispatch, quote, {
                    order,
                    defer,
                    landing: { kind: 'resume' },
                    announce: true,
                });
                setPhase('ready');
            } catch (error) {
                if (isCurrent() && !(error instanceof RetiredSaveError)) setPhase('reload-failed');
            }
        };
        void run();
        return () => {
            superseded = true;
        };
    }, [phase, settled, laneReady, store, dispatch, order, defer]);

    const confirmReload = useCallback(() => {
        if (phase === 'conflict' || conflict !== null) dispatch(reloadDraft());
        setWizardShown(false);
        setPhase('reloading');
    }, [phase, conflict, dispatch]);

    const retryMountLoad = useCallback(() => {
        loadMode.current = 'refetch';
        setPhase('mount-loading');
    }, []);

    return { phase, wizardShown, conflictStatus, confirmReload, retryMountLoad };
};
